"use client"

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { Sidebar } from './sidebar'
import { MobileTopbar } from './mobile-topbar'
import { useSessionExtension } from '@/lib/hooks/use-session-extension'

interface MainLayoutProps {
  children: React.ReactNode
}

export function MainLayout({ children }: MainLayoutProps) {
  useSessionExtension()

  // Off-canvas mobile sidebar visibility. State lives here so the topbar's
  // hamburger and the sidebar itself can stay in sync.
  const [mobileOpen, setMobileOpen] = useState(false)
  const pathname = usePathname()

  // Close on navigation so tapping a nav item dismisses the drawer.
  useEffect(() => { setMobileOpen(false) }, [pathname])

  // Sync to body so CSS can drive sidebar slide-in + backdrop visibility,
  // and lock scroll while the drawer is open.
  useEffect(() => {
    if (typeof document === 'undefined') return
    if (mobileOpen) {
      document.body.setAttribute('data-mobile-open', '1')
      document.body.style.overflow = 'hidden'
    } else {
      document.body.removeAttribute('data-mobile-open')
      document.body.style.overflow = ''
    }
    return () => {
      document.body.removeAttribute('data-mobile-open')
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  // ESC closes the drawer.
  useEffect(() => {
    if (!mobileOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileOpen(false) }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [mobileOpen])

  return (
    <div className="of-shell">
      <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />
      {/* Backdrop sits under the sidebar but over the page when drawer is open */}
      <button
        type="button"
        className="of-sidebar-backdrop"
        aria-hidden={!mobileOpen}
        tabIndex={-1}
        onClick={() => setMobileOpen(false)}
      />
      <div className="of-shell-main">
        <MobileTopbar onMenuClick={() => setMobileOpen(true)} />
        <main className="of-page-main overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  )
}
